/**
 * Food Specialty Item Component
 * Displays individual food specialty card with image and description
 * Follows Single Responsibility Principle - only responsible for rendering a single food item
 */

import React from 'react';
import {
  typographyColors,
  spacing,
  borders,
  typography,
  backgroundColors,
  strokeColors,
} from '@/design-system/utils';
import { ImageThumbnail } from '@/components/ui/image-thumbnail';
import { ExpandableText } from '@/components/ui/expandable-text';
import { Heading } from '@/components/ui/heading';

export interface FoodSpecialty {
  id: string;
  name: string;
  description: string;
  image: string | null;
}

export interface FoodSpecialtyItemProps {
  /**
   * Food specialty data
   */
  food: FoodSpecialty;
  /**
   * Place name for image fetching context
   */
  placeName?: string;
}

/**
 * Food Specialty Item Component
 * Renders a single food specialty card with image, title, and expandable description
 */
export const FoodSpecialtyItem: React.FC<FoodSpecialtyItemProps> = ({
  food,
  placeName,
}) => {
  // Debug logging for food specialty item
  React.useEffect(() => {
    console.log('🍽️ [FoodSpecialtyItem] Rendering food specialty:', {
      id: food.id,
      name: food.name,
      image: food.image,
      hasImage: !!food.image,
      placeName,
      willFetchImage: !food.image,
    });
  }, [food.id, food.name, food.image, placeName]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: '1 0 0',
        borderRadius: borders.radius.xl,
        border: `${borders.width.thin} solid ${strokeColors.foodSpecialty}`,
        background: backgroundColors.foodSpecialty,
        overflow: 'hidden',
      }}
    >
      {/* Image Container */}
      <div
        style={{
          display: 'flex',
          height: '113px',
          width: '100%',
        }}
      >
        <ImageThumbnail
          image={food.image}
          alt={food.name}
          title={food.name}
          description={food.description}
          placeName={placeName}
          category='Local food specialities'
          width='100%'
          height='100%'
          borderRadius={borders.radius.lg}
          style={{ padding: 0 }}
        />
      </div>
      {/* Content */}
      <div
        style={{
          padding: spacing[2],
          display: 'flex',
          flexDirection: 'column',
          gap: spacing[2],
          flex: 1,
        }}
      >
        <Heading
          as='h4'
          fontSize={typography.size.base}
          fontWeight={typography.weight.semibold}
          color={typographyColors.text}
          lineHeight='normal'
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {food.name}
        </Heading>
        <ExpandableText
          text={food.description}
          maxLines={2}
          fontSize={typography.size.sm}
          fontWeight={typography.weight.regular}
          color={typographyColors.text}
          lineHeight={typography.lineHeight.relaxed}
        />
      </div>
    </div>
  );
};
